import {Observable, of} from 'rxjs';

import {
    ApiError,
    ServerResponse,
    ServerResponseData,
    HttpStatusCodesEnum,
    AccountSubTypeEnum,
    ClientUserStatusesEnum,
    LinkedAccountStatusesEnum,
    TradingAccountStatusesEnum,
    LinkedAccountsService,
    CompanyEmployeesService,
    IndividualSubordinatesService,
    TradingClientInterface,
    LinkedAccountInterface,
    CompanyEmployeeInterface,
    IndividualSubordinateInterface,
    UserModel,
    TradingGroupModel,
    TradingClientModel,
    LinkedAccountModel,
    TradingAccountModel,
    CompanyEmployeeModel,
    IndividualSubordinateModel,
    CashAccountDetailedReportModel,
    TradingAccountDetailedReportModel,
} from '@spa/api';

import {map, switchMap} from 'rxjs/operators';

import {Helper} from '../../../../shared/classes';
import {AlertsService} from '../../../../shared/services';
import {PermissionRuleInterface} from '../../../../shared/interfaces';
import {AlertTypesEnum, PermissionRuleCheckStrategiesEnum, PermissionsEnum} from '../../../../shared/enums';

import {AssignAccountRightsSelectableEntityInterface} from '../../interfaces';
import {PanelAccountsEditResolverErrorStatesEnum, TradingAccountTypesEnum} from '../../enums';

import {PanelAccountsEditMessages} from '../../../../../assets/messages';

export class PanelAccountsEditTrait {
    static getLinkedAccounts(
        account: TradingAccountDetailedReportModel | CashAccountDetailedReportModel,
        linkedAccountsService: LinkedAccountsService,
        alertsService: AlertsService
    ): Observable<LinkedAccountModel[]> {
        const request = !account.isOwnerCompany ? linkedAccountsService.getByIndividualId(account.ownerId) :
            linkedAccountsService.getByCompanyId(account.ownerId);

        return request.pipe(
            switchMap((response: ServerResponseData<LinkedAccountInterface[]>) => {
                /**
                 * Error checks don't stop process, because this code is using in opening of transfer dialogs,
                 * which contains ability to create internal transfer additional to bank transfer.
                 * Linked accounts are needed only for bank transfer.
                 */
                const isStatusOk = (response.status === HttpStatusCodesEnum.Ok);

                if (!isStatusOk && account.isCashAccount) {
                    alertsService.addAlert(
                        PanelAccountsEditMessages.GetLinkedAccountsFailed.message,
                        AlertTypesEnum.Warning
                    );
                }

                const invalidArrayCheck =
                    Helper.errorsHelper().checkForInvalidRespondedArray<LinkedAccountInterface>(response);

                if (isStatusOk && invalidArrayCheck && account.isCashAccount) {
                    alertsService.addAlert(
                        PanelAccountsEditMessages.OwnerLinkedAccountsNotFound.message,
                        AlertTypesEnum.Warning
                    );
                }

                let linkedAccounts = [];

                if (isStatusOk && Array.isArray(response.data) && (response.data.length > 0)) {
                    const data = response.data as LinkedAccountInterface[];

                    /** Get only active bank accounts. With inactive accounts bank transfer cannot be performed. */
                    linkedAccounts = data.map((item: LinkedAccountInterface) => new LinkedAccountModel(item))
                        .filter((item: LinkedAccountModel) => (item.status === LinkedAccountStatusesEnum.Active));

                    if ((linkedAccounts.length === 0) && account.isCashAccount) {
                        alertsService.addAlert(
                            PanelAccountsEditMessages.OwnerActiveLinkedAccountsNotFound.message,
                            AlertTypesEnum.Warning
                        );
                    }
                }

                return of(linkedAccounts);
            })
        );
    }

    static getClientUsers(
        account: TradingAccountDetailedReportModel | CashAccountDetailedReportModel,
        companyEmployeesService: CompanyEmployeesService,
        individualSubordinatesService: IndividualSubordinatesService,
    ): Observable<{ status: boolean, error: string, data: AssignAccountRightsSelectableEntityInterface[] }> {
        const request = !account.isOwnerCompany ? individualSubordinatesService.getAllByIndividualId(account.ownerId) :
            companyEmployeesService.getAllByCompanyId(account.ownerId);


        return (request as ServerResponse<(IndividualSubordinateInterface | CompanyEmployeeInterface)[]>).pipe(
            map((response: ServerResponseData<(IndividualSubordinateInterface | CompanyEmployeeInterface)[]>) => {
                if (response.status !== HttpStatusCodesEnum.Ok) {
                    return {status: false, error: PanelAccountsEditMessages.GetOwnerUsersFailed.message, data: null};
                }

                const isInvalidData = Helper.errorsHelper()
                    .checkForInvalidRespondedArray<IndividualSubordinateInterface | CompanyEmployeeInterface>(response);

                if (isInvalidData) {
                    return {status: false, error: PanelAccountsEditMessages.GetOwnerUsersNotFound.message, data: null};
                }

                const data = (response.data as (IndividualSubordinateInterface | CompanyEmployeeInterface)[])
                    .map((item: IndividualSubordinateInterface | CompanyEmployeeInterface) => {
                        return !account.isOwnerCompany ? new IndividualSubordinateModel(item as IndividualSubordinateInterface) :
                            new CompanyEmployeeModel(item as CompanyEmployeeInterface);
                    }).filter((clientUser: IndividualSubordinateModel | CompanyEmployeeModel) => {
                        return clientUser.status === ClientUserStatusesEnum.Active;
                    }).map((clientUser: IndividualSubordinateModel | CompanyEmployeeModel) => {
                        return {
                            id: clientUser.id,
                            name: [clientUser.firstName, clientUser.lastName].join(' '),
                            reference: account.accountReferenceId,
                        } as AssignAccountRightsSelectableEntityInterface;
                    });

                if (data.length === 0) {
                    return {status: false, error: PanelAccountsEditMessages.GetOwnerHasNotActiveUsers.message, data: null};
                }

                return {status: true, error: null, data};
            })
        );
    }

    static showSearchClientWarningsIfNeeded(
        isStatusOk: boolean,
        response: ServerResponseData<TradingClientInterface[]>,
        alertsService: AlertsService
    ): void {
        if (!isStatusOk) {
            alertsService.addAlert(PanelAccountsEditMessages.SearchClientFailed.message, AlertTypesEnum.Warning);
        }

        const invalidArrayCheck =
            Helper.errorsHelper().checkForInvalidRespondedArray<TradingClientInterface>(response);

        if (isStatusOk && invalidArrayCheck) {
            alertsService.addAlert(PanelAccountsEditMessages.SearchClientFailed.message, AlertTypesEnum.Warning);
        }
    }

    static getActiveAccounts(
        tradingClient: TradingClientModel,
        currentAccountId: number
    ): TradingAccountModel[] {
        const currentAccount = tradingClient.getGroupAccountById(currentAccountId);

        return tradingClient.groups.filter((group: TradingGroupModel) => {
            group.accounts = group.accounts.filter((account: TradingAccountModel) => {
                return [
                    TradingAccountStatusesEnum.ForApproval,
                    TradingAccountStatusesEnum.Rejected,
                    TradingAccountStatusesEnum.Blocked,
                    TradingAccountStatusesEnum.Closed
                ].indexOf(account.status) < 0;
            });

            return (group.accounts.length > 0);
        }).reduce((concatenatedAccounts: TradingAccountModel[], group: TradingGroupModel) => {
            concatenatedAccounts = concatenatedAccounts.concat(...group.accounts);

            return concatenatedAccounts;
        }, []).filter((account: TradingAccountModel) => {
            /**
             * If current account isn't cash account, the transfers can be performed only with its related cash account
             * and all trading accounts with the same related cash account.
             */
            if (!currentAccount.isCashAccount) {
                return !account.isCashAccount ? (account.relatedCashAccountId === currentAccount.relatedCashAccountId) :
                    (account.id === currentAccount.relatedCashAccountId);
            }

            /**
             * If the account is cash, the transfers can be performed with other cash accounts and its related trading
             * accounts
             */
            return account.isCashAccount || (account.relatedCashAccountId === currentAccount.id);
        });
    }

    static showPostSearchClientWarningsIfNeeded(accounts: TradingAccountModel[], alertsService: AlertsService): void {
        if ([0, 1].indexOf(accounts.length) >= 0) {
            alertsService.addAlert(
                PanelAccountsEditMessages.OwnerActiveAccountsNotFound.message,
                AlertTypesEnum.Warning
            );
        }
    }

    static getInitialErrorMessages(errors: ApiError[]): string[] {
        return errors.map((error: ApiError) => {
            switch (error.status) {
                case PanelAccountsEditResolverErrorStatesEnum.GetAccountDetailsFailed:
                    return PanelAccountsEditMessages.GetAccountDetailsFailed.message;
                case PanelAccountsEditResolverErrorStatesEnum.TradingAccountNotFound:
                    return PanelAccountsEditMessages.AccountDetailsNotFound.message;
                case PanelAccountsEditResolverErrorStatesEnum.GetCashAccountDetailsFailed:
                    return PanelAccountsEditMessages.GetMasterAccountDetailsFailed.message;
                case PanelAccountsEditResolverErrorStatesEnum.CashAccountNotFound:
                    return PanelAccountsEditMessages.MasterAccountDetailsNotFound.message;
                case PanelAccountsEditResolverErrorStatesEnum.GetMasterTradingAccountsFailed:
                    return PanelAccountsEditMessages.GetMasterTradingAccountsFailed.message;
                case PanelAccountsEditResolverErrorStatesEnum.GetClientFailed:
                    return PanelAccountsEditMessages.GetClientFailed.message;
                default:
                    return PanelAccountsEditMessages.UnknownError.message;
            }
        });
    }

    static checkVisibilityTransferButton(
        accountSubType: number,
        authenticatedUser: UserModel,
        transferPermissionRule: PermissionRuleInterface,
    ): boolean {
        const subTypeCheck = [
            AccountSubTypeEnum.Trading,
            AccountSubTypeEnum.TradingCash
        ].includes(accountSubType);

        let permissionCheck = false;
        const { permissions } = transferPermissionRule;

        if (transferPermissionRule.strategy === PermissionRuleCheckStrategiesEnum.Intersect) {
            permissionCheck = Helper.getIntersectStrategyPermissionsCheck(permissions, authenticatedUser.permissions);
        } else {
            permissionCheck = Helper.getUnionStrategyPermissionsCheck(permissions, authenticatedUser.permissions);
        }

        if (!permissionCheck && authenticatedUser.permissions.includes(PermissionsEnum.CrmAllAll)) {
            return true;
        }

        return (permissionCheck && subTypeCheck);
    }

    static getViewDetailsPermissionRule(type: number): PermissionRuleInterface {
        if (type !== TradingAccountTypesEnum.Cash) {
            return {
                permissions: Helper.enumsHelper().permissionsEnumHelper().getDetailsViewPermissionsByAccountType(
                    TradingAccountTypesEnum.Trading
                ),
                strategy: PermissionRuleCheckStrategiesEnum.Union
            };
        }

        return {
            permissions: Helper.enumsHelper().permissionsEnumHelper().getDetailsViewPermissionsByAccountType(
                TradingAccountTypesEnum.Cash
            ),
            strategy: PermissionRuleCheckStrategiesEnum.Union
        };
    }

    static getViewFeePlansPermissionRule(accountType: number): PermissionRuleInterface {
        return {
            permissions: Helper.enumsHelper().permissionsEnumHelper().getFeePlansViewPermissions(accountType),
            strategy: PermissionRuleCheckStrategiesEnum.Union
        };
    }

    static getViewCurrentPositionsPermissionRule(): PermissionRuleInterface {
        return {
            permissions: Helper.enumsHelper().permissionsEnumHelper().getCurrentPositionsViewPermissions(),
            strategy: PermissionRuleCheckStrategiesEnum.Union
        };
    }

    static getRestrictUpdateStatusToClosedPermissionRule(type): PermissionRuleInterface {
        if (type !== TradingAccountTypesEnum.Cash) {
            return {
                permissions: [
                    PermissionsEnum.CrmAllAll,
                    PermissionsEnum.CrmTradingAccountsDelete
                ],
                strategy: PermissionRuleCheckStrategiesEnum.Intersect
            };
        }

        return {
            permissions: [
                PermissionsEnum.CrmAllAll,
                PermissionsEnum.CrmCashAccountsDelete
            ],
            strategy: PermissionRuleCheckStrategiesEnum.Union
        };
    }

    static getUpdateAccountStatusPermissionRule(type: number): PermissionRuleInterface {
        if (type !== TradingAccountTypesEnum.Cash) {
            return {
                permissions: [PermissionsEnum.CrmTradingAccountsUpdateStatus],
                strategy: PermissionRuleCheckStrategiesEnum.Intersect
            };
        }

        return {
            permissions: [PermissionsEnum.CrmCashAccountsUpdateStatus],
            strategy: PermissionRuleCheckStrategiesEnum.Intersect
        };
    }

    static getWithdrawalPermissionRule(): PermissionRuleInterface {
        return {
            permissions: [PermissionsEnum.CrmPaymentsCreateNewWithdrawal],
            strategy: PermissionRuleCheckStrategiesEnum.Intersect
        };
    }

    static getDepositPermissionRule(): PermissionRuleInterface {
        return {
            permissions: [PermissionsEnum.CrmPaymentsCreateNewDeposit],
            strategy: PermissionRuleCheckStrategiesEnum.Intersect
        };
    }

    static getTransferPermissionRule(): PermissionRuleInterface {
        return {
            permissions: [PermissionsEnum.CrmPaymentsCreateNewTransfer],
            strategy: PermissionRuleCheckStrategiesEnum.Intersect
        };
    }

    static getAddUserPermissionRule(): PermissionRuleInterface {
        return {
            permissions: [PermissionsEnum.CrmUsersUpdateRights],
            strategy: PermissionRuleCheckStrategiesEnum.Intersect
        };
    }

    static getCreateAdjustmentRule(): PermissionRuleInterface {
        return {
            permissions: [PermissionsEnum.CrmPaymentsCreateNewAdjustment],
            strategy: PermissionRuleCheckStrategiesEnum.Intersect
        };
    }

    static getCreateOrderPermissionRule(): PermissionRuleInterface {
        return {
            permissions: [PermissionsEnum.CrmFundOrdersCreate],
            strategy: PermissionRuleCheckStrategiesEnum.Intersect
        };
    }

    static getUploadPositionPermissionRule(): PermissionRuleInterface {
        return {
            permissions: [PermissionsEnum.CrmFundExecutionsCreate],
            strategy: PermissionRuleCheckStrategiesEnum.Intersect
        };
    }

    static getMatchingOrdersPermissionRule(): PermissionRuleInterface {
        return {
            permissions: [PermissionsEnum.CrmFundOrdersUpdate],
            strategy: PermissionRuleCheckStrategiesEnum.Union
        };
    }
}
